import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const Marcas = () => {
  const [marcas, setMarcas] = useState([]);
  const [originalMarcas, setOriginalMarcas] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedMarca, setSelectedMarca] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    id_pet: '',
    nombre_marca: '',
    habilitada: true,
  });
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [userIdRol, setUserIdRol] = useState(null);
  const [userSucursales, setUserSucursales] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();

  // Decodificar token JWT para obtener id_pet, id_rol y sucursales
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Payload del token:', payload);
        setUserIdPet(payload.id_pet || null);
        setUserIdRol(payload.id_rol || null);
        setUserSucursales(payload.sucursales || []);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        const errorMsg = 'Error al decodificar el token de usuario';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      }
    } else {
      const errorMsg = 'No se encontró un token de usuario';
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  }, []);

  // Formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Normalizar datos de marca
  const normalizeMarca = (marca) => {
    if (!marca || !marca.id_marca) {
    console.warn('Marca inválida recibida:', marca);
    return null;
    }

    // **CORRECCIÓN CLAVE:** Usar Boolean() para manejar 1, 0, true, false
    const habilitadaValue = Boolean(marca.habilitada); 

    return {
    id_marca: marca.id_marca ?? null,
    id_pet: marca.id_pet ?? null,
    nombre_marca: marca.nombre_marca ?? '',
    habilitada: habilitadaValue, // Aplicamos el valor booleano garantizado
    createdAt: marca.createdAt ?? null,
    updatedAt: marca.updatedAt ?? null,
    };
    };

  // Obtener marcas y pets
  useEffect(() => {
    const fetchData = async () => {
      if (!userIdPet || !userIdRol) {
        console.log('Esperando id_pet o id_rol del usuario...');
        return;
      }
      try {
        setLoading(true);
        const marcasRes = await api.get('/marcas/');
        console.log('Datos crudos de GET /marcas:', marcasRes.data);
        const marcasData = Array.isArray(marcasRes.data)
          ? marcasRes.data.filter(item => item !== null && item !== undefined)
          : [];
        const allowedPets = [userIdPet, ...userSucursales];
        const normalizedMarcas = marcasData
          .map(normalizeMarca)
          .filter(marca => marca !== null && marca.id_marca && allowedPets.includes(marca.id_pet));
        console.log('Marcas normalizadas:', normalizedMarcas);
        setMarcas(normalizedMarcas);
        setOriginalMarcas(normalizedMarcas);

        const petsRes = await api.get('/pets/');
        console.log('Respuesta de pets:', petsRes.data);
        setPets(Array.isArray(petsRes.data) ? petsRes.data.filter(pet => allowedPets.includes(pet.id_pet)) : []);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        const errorMsg = 'Error al cargar datos: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [userIdPet, userIdRol, userSucursales]);

  // Buscar marcas en frontend
  useEffect(() => {
    const searchMarcas = () => {
      console.log('Buscando con término:', searchTerm);
      console.log('Marcas originales:', originalMarcas);
      if (!searchTerm) {
        setMarcas(originalMarcas);
        console.log('Restaurando marcas:', originalMarcas);
        return;
      }
      const filteredMarcas = originalMarcas
        .filter(marca => marca && marca.id_marca && marca.nombre_marca)
        .filter(marca => marca.nombre_marca.toLowerCase().includes(searchTerm.toLowerCase()));
      console.log('Marcas filtradas:', filteredMarcas);
      setMarcas(filteredMarcas);
      setPage(0);
    };
    searchMarcas();
  }, [searchTerm, originalMarcas]);

  // Abrir modal para crear/editar marca
  const handleOpenModal = (marca = null) => {
    setSelectedMarca(marca);
    setFormData(
      marca
        ? normalizeMarca(marca) || {
            id_pet: userIdPet || '',
            nombre_marca: '',
            habilitada: true,
          }
        : {
            id_pet: userIdPet || '',
            nombre_marca: '',
            habilitada: true,
          }
    );
    setOpenModal(true);
  };

  // Cerrar Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  // Guardar marca (crear o editar)
  const handleSave = async () => {
    try {
      if (!formData.nombre_marca || !formData.id_pet) {
        const errorMsg = 'Nombre de la marca y veterinaria son obligatorios';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        return;
      }
      const payload = {
        id_pet: formData.id_pet,
        nombre_marca: formData.nombre_marca,
        habilitada: formData.habilitada,
      };
      console.log('Guardando marca, payload:', payload);
      if (selectedMarca) {
        const response = await api.put(`/marcas/${selectedMarca.id_marca}`, payload);
        console.log('Respuesta de PUT /marcas:', response.data);
        const updatedMarca = normalizeMarca(response.data);
        if (updatedMarca) {
          const newMarcas = marcas.map(m => (m.id_marca === selectedMarca.id_marca ? updatedMarca : m));
          setMarcas(newMarcas);
          setOriginalMarcas(newMarcas);
          setSnackbarMessage('Marca actualizada exitosamente');
          setSnackbarOpen(true);
        }
      } else {
        const response = await api.post('/marcas/', payload);
        console.log('Respuesta de POST /marcas:', response.data);
        const newMarca = normalizeMarca(response.data);
        if (newMarca) {
          const newMarcas = [...marcas, newMarca];
          setMarcas(newMarcas);
          setOriginalMarcas(newMarcas);
          setSnackbarMessage('Marca creada exitosamente');
          setSnackbarOpen(true);
        }
      }
      setOpenModal(false);
      setError(null);
      setPage(0);
    } catch (error) {
      console.error('Error al guardar marca:', error);
      const errorMsg = 'Error al guardar marca: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Deshabilitar marca
  const handleToggleHabilitada = async (id, habilitada) => {
    try {
      const marca = marcas.find(m => m.id_marca === id);
      if (!marca) {
        throw new Error('Marca no encontrada en el estado local');
      }
      const payload = {
        id_pet: marca.id_pet,
        nombre_marca: marca.nombre_marca,
        habilitada: !habilitada,
      };
      console.log('Enviando PUT /marcas/', id, 'con payload:', payload);
      const response = await api.put(`/marcas/${id}`, payload);
      console.log('Respuesta de PUT /marcas:', response.data);
      const updatedMarca = normalizeMarca(response.data, !habilitada);
      if (updatedMarca) {
        const newMarcas = marcas.map(m => (m.id_marca === id ? updatedMarca : m));
        console.log('Nuevo estado de marcas:', newMarcas);
        setMarcas(newMarcas);
        setOriginalMarcas(newMarcas);
        setSnackbarMessage(`Marca ${updatedMarca.habilitada ? 'habilitada' : 'deshabilitada'} exitosamente`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error, 'Respuesta:', error.response);
      const errorMsg = 'Error al actualizar estado: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Eliminar marca
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta marca?')) return;
    try {
      console.log('Enviando DELETE /marcas/', id);
      const response = await api.delete(`/marcas/${id}`);
      console.log('Respuesta de DELETE /marcas:', response.data);
      const newMarcas = marcas.filter(m => m.id_marca !== id);
      setMarcas(newMarcas);
      setOriginalMarcas(newMarcas);
      setSnackbarMessage('Marca eliminada exitosamente');
      setSnackbarOpen(true);
      setPage(0);
    } catch (error) {
      console.error('Error al eliminar marca:', error);
      const errorMsg = 'Error al eliminar marca: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Manejo de paginación
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const paginatedMarcas = marcas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Marcas
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar marcas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} disableGutters>
        {loading || !userIdPet || !userIdRol ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : marcas.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin datos</Typography>
            <Typography color="text.secondary">
              No se encontraron marcas. Crea una nueva marca para comenzar.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ mt: 2 }}
            >
              Nueva Marca
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Nueva Marca
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  boxShadow: 3,
                }}
              >
                <Table
                  sx={{
                    tableLayout: 'auto',
                  }}
                  aria-label="tabla de marcas"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Nombre Marca
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Veterinaria
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Habilitada
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Creado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Actualizado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedMarcas.map((row) => {
                      if (!row || !row.id_marca) {
                        console.warn('Fila inválida en la tabla:', row);
                        return null;
                      }
                      return (
                        <TableRow
                          key={row.id_marca}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.id_marca}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, fontWeight: 'bold' }}>
                            {row.nombre_marca}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {pets.find(p => p.id_pet === row.id_pet)?.nombre_veterinaria || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <Switch
                              checked={row.habilitada ?? false}
                              onChange={() => handleToggleHabilitada(row.id_marca, row.habilitada ?? false)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.updatedAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <IconButton
                              onClick={() => handleOpenModal(row)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDelete(row.id_marca)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                <Button
                  variant="contained"
                  disabled={page === 0}
                  onClick={() => handleChangePage(page - 1)}
                >
                  Anterior
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Página {page + 1} de {Math.ceil(marcas.length / rowsPerPage)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={page >= Math.ceil(marcas.length / rowsPerPage) - 1}
                  onClick={() => handleChangePage(page + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogTitle>{selectedMarca ? 'Editar Marca' : 'Nueva Marca'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Nombre de la Marca"
              fullWidth
              value={formData.nombre_marca}
              onChange={(e) => setFormData({ ...formData, nombre_marca: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              select
              label="Veterinaria"
              fullWidth
              value={formData.id_pet}
              onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
              sx={{ mt: 2 }}
              required
            >
              {pets.length > 0 ? (
                pets.map((pet) => (
                  <MenuItem key={pet.id_pet} value={pet.id_pet}>
                    {pet.nombre_veterinaria}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay veterinarias disponibles
                </MenuItem>
              )}
            </TextField>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={formData.habilitada}
                onChange={(e) => setFormData({ ...formData, habilitada: e.target.checked })}
                color="primary"
              />
              <Typography>Habilitada</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.nombre_marca || !formData.id_pet}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Marcas;